import { Link } from "react-router-dom";
import {
  ArrowRight, Check,
  FileText, PencilLine, Globe2, Headphones, Mic, Subtitles, AudioLines,
  ScanText, FileSearch, Languages as LangIcon, ClipboardCheck, Database,
  Search, Users, Cpu, ShieldCheck, FileCheck2, Workflow,
} from "lucide-react";
import { LANGUAGE_SERVICES, AI_SERVICES } from "@/data/content";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";

const ICONS = {
  FileText, PencilLine, Globe2, Headphones, Mic, Subtitles, AudioLines,
  ScanText, FileSearch, Languages: LangIcon, ClipboardCheck, Database,
};

/* =========================================================
   Shared PageHero — dense, dark, enterprise
   ========================================================= */
export function PageHero({ eyebrow, title, subtitle, accent = false }) {
  return (
    <section
      data-testid="page-hero"
      className="relative pt-16 lg:pt-[68px] bg-[#071D49] text-white overflow-hidden"
    >
      <div className="absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full bg-[#00D1B2]/10 blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-[460px] h-[460px] rounded-full bg-[#1A4E8A]/30 blur-3xl" />
      <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <Reveal variant="up">
          <p
            className={`text-[11px] tracking-[0.22em] uppercase font-semibold ${
              accent ? "text-[#00D1B2]" : "text-[#00D1B2]"
            }`}
          >
            {eyebrow}
          </p>
          <h1 className="mt-4 text-4xl lg:text-[56px] font-semibold tracking-tight leading-[1.05] text-white max-w-4xl">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-5 text-[15px] lg:text-base text-white/75 leading-relaxed max-w-2xl">
              {subtitle}
            </p>
          )}
        </Reveal>
      </div>
    </section>
  );
}

/* =========================================================
   Alternating service block (image + text)
   ========================================================= */
function ServiceBlock({ s, index, accent = false }) {
  const Icon = ICONS[s.icon] || FileText;
  const flip = index % 2 === 1;
  return (
    <article
      id={s.slug}
      data-testid={`service-block-${s.slug}`}
      className="scroll-mt-24 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center"
    >
      <Reveal
        variant={flip ? "left" : "right"}
        className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}
      >
        <div className="img-zoom relative rounded-md overflow-hidden border border-[#E5E7EB] bg-[#F7F8FA]">
          <img
            src={s.image}
            alt={s.title}
            className="w-full h-[300px] lg:h-[360px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#071D49]/45 via-transparent to-transparent" />
          <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-white/95 backdrop-blur px-3 py-1.5 rounded-md">
            <Icon className={`w-4 h-4 ${accent ? "text-[#00D1B2]" : "text-[#1A4E8A]"}`} />
            <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#071D49]">
              {accent ? "AI Solution" : "Language Service"}
            </span>
          </div>
        </div>
      </Reveal>

      <Reveal
        variant={flip ? "right" : "left"}
        className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}
      >
        <p className={`text-[11px] tracking-[0.22em] uppercase font-semibold ${accent ? "text-[#00D1B2]" : "text-[#1A4E8A]"}`}>
          {String(index + 1).padStart(2, "0")} — {accent ? "AI Solution" : "Language Service"}
        </p>
        <h3 className="mt-3 text-2xl lg:text-[32px] font-semibold tracking-tight text-[#071D49] leading-[1.12]">
          {s.title}
        </h3>
        <p className="mt-4 text-[14.5px] text-[#5B6472] leading-relaxed max-w-xl">
          {s.description}
        </p>
        <Link
          to="/contact"
          data-testid={`service-cta-${s.slug}`}
          className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#1A4E8A] hover:text-[#00A990] link-ink w-fit"
        >
          Request a Quote <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </Reveal>
    </article>
  );
}

/* =========================================================
   Process schema (animated workflow line)
   ========================================================= */
function ProcessSchema({ steps, accent = false }) {
  const color = accent ? "#00D1B2" : "#1A4E8A";
  return (
    <div className="relative">
      <div
        className="hidden lg:block absolute top-[34px] left-[6%] right-[6%] h-px"
        style={{ background: `linear-gradient(to right, ${color}40, ${color}, ${color}40)` }}
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
        {steps.map((step, i) => (
          <Reveal variant="up" delay={i + 1} key={step.t}>
            <div className="relative bg-white border border-[#E5E7EB] rounded-md p-5 hover:border-[--c] transition-colors h-full"
                 style={{ "--c": color }}>
              <div
                className="absolute -top-3 left-5 w-7 h-7 rounded-md flex items-center justify-center text-white text-[11px] font-semibold tracking-wider"
                style={{ background: color }}
              >
                {String(i + 1).padStart(2, "0")}
              </div>
              <step.icon className="w-5 h-5 mt-3 mb-3" style={{ color }} />
              <h4 className="text-[14px] font-semibold text-[#071D49] leading-tight">{step.t}</h4>
              <p className="mt-1.5 text-[12.5px] text-[#5B6472] leading-snug">{step.b}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

const LANGUAGE_PROCESS = [
  { icon: Search, t: "Scope & Quote", b: "Map content, languages, SLA. Reply in 2 business hours." },
  { icon: Users, t: "Linguist Match", b: "Native specialists vetted for your industry vertical." },
  { icon: FileText, t: "Translation", b: "First-pass translation with terminology DB & TM." },
  { icon: FileCheck2, t: "Independent Review", b: "Second native linguist reviews accuracy & tone." },
  { icon: ShieldCheck, t: "Delivery & QA", b: "Certified output, NDA-protected, format-ready." },
];

const AI_PROCESS = [
  { icon: Workflow, t: "Pipeline Audit", b: "Understand your LLM, MT engine, or AI content workflow." },
  { icon: Cpu, t: "AI Output Ingest", b: "Receive machine-generated content across 85+ languages." },
  { icon: Users, t: "Human Validation", b: "Native specialists evaluate, annotate, or post-edit." },
  { icon: ClipboardCheck, t: "Scoring & Feedback", b: "Structured accuracy + cultural risk scoring." },
  { icon: Database, t: "Clean Data Out", b: "Validated multilingual data ready for production." },
];

/* =========================================================
   Page
   ========================================================= */
export default function Services() {
  return (
    <div data-testid="services-page" className="bg-white">
      <Seo
        title="Language Services & AI Solutions"
        description="Certified translation, localization, interpretation, and AI-driven language quality assurance — every service Petraloc delivers, end to end."
        path="/services"
      />

      <PageHero
        eyebrow="Services"
        title="Language services, enhanced by AI."
        subtitle="Two divisions — human translation, localization, and interpretation; plus AI quality assurance, evaluation, and multilingual data services. One enterprise quality standard."
      />

      {/* LANGUAGE SERVICES — alternating blocks */}
      <section className="py-14 lg:py-20 bg-white" id="language">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
          <Reveal variant="up" className="max-w-2xl mb-12">
            <p className="eyebrow">Division 01 — Language</p>
            <h2 className="mt-3 text-3xl lg:text-[40px] font-semibold tracking-tight text-[#071D49] leading-[1.1]">
              Human translation, localization & voice.
            </h2>
            <p className="mt-4 text-[15px] text-[#5B6472] leading-relaxed max-w-xl">
              Native specialists with domain expertise — every deliverable verified by an independent second pass.
            </p>
          </Reveal>

          <div className="space-y-16 lg:space-y-20">
            {LANGUAGE_SERVICES.map((s, i) => (
              <ServiceBlock key={s.slug} s={s} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* LANGUAGE PROCESS SCHEMA */}
      <section className="py-14 lg:py-16 bg-[#F7F8FA] border-y border-[#E5E7EB]">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
          <Reveal variant="up" className="max-w-2xl mb-12">
            <p className="eyebrow">Language Process</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-[#071D49] leading-[1.15]">
              Five-step delivery, every project.
            </h2>
            <p className="mt-3 text-[14.5px] text-[#5B6472] leading-relaxed">
              A standardized human-led workflow that scales from a single certificate to enterprise content programs.
            </p>
          </Reveal>
          <ProcessSchema steps={LANGUAGE_PROCESS} />
        </div>
      </section>

      {/* AI LANGUAGE SOLUTIONS — alternating blocks on dark */}
      <section className="py-14 lg:py-20 bg-[#04132F] text-white relative overflow-hidden" id="ai">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#00D1B2]/8 blur-3xl" />
        <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
          <Reveal variant="up" className="max-w-2xl mb-12">
            <p className="eyebrow-teal">Division 02 — AI Solutions</p>
            <h2 className="mt-3 text-3xl lg:text-[40px] font-semibold tracking-tight text-white leading-[1.1]">
              AI quality assurance, evaluation & multilingual data.
            </h2>
            <p className="mt-4 text-[15px] text-white/70 leading-relaxed max-w-xl">
              Native specialists review, validate, and annotate AI-generated content across 85+ languages — keeping LLMs and translation engines accurate, on-brand, and culturally precise.
            </p>
          </Reveal>

          <div className="space-y-16 lg:space-y-20">
            {AI_SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon] || FileSearch;
              const flip = i % 2 === 1;
              return (
                <article
                  id={s.slug}
                  key={s.slug}
                  data-testid={`service-block-${s.slug}`}
                  className="scroll-mt-24 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center"
                >
                  <Reveal
                    variant={flip ? "left" : "right"}
                    className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}
                  >
                    <div className="img-zoom relative rounded-md overflow-hidden border border-white/10">
                      <img src={s.image} alt={s.title} className="w-full h-[300px] lg:h-[360px] object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#04132F] via-[#04132F]/40 to-transparent" />
                      <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-[#00D1B2]/15 backdrop-blur border border-[#00D1B2]/30 px-3 py-1.5 rounded-md">
                        <Icon className="w-4 h-4 text-[#00D1B2]" />
                        <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#00D1B2]">
                          AI Solution
                        </span>
                      </div>
                    </div>
                  </Reveal>

                  <Reveal
                    variant={flip ? "right" : "left"}
                    className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}
                  >
                    <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#00D1B2]">
                      {String(i + 1).padStart(2, "0")} — AI Solution
                    </p>
                    <h3 className="mt-3 text-2xl lg:text-[32px] font-semibold tracking-tight text-white leading-[1.12]">
                      {s.title}
                    </h3>
                    <p className="mt-4 text-[14.5px] text-white/70 leading-relaxed max-w-xl">
                      {s.description}
                    </p>
                    <Link
                      to="/contact"
                      data-testid={`service-cta-${s.slug}`}
                      className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#00D1B2] hover:text-white link-ink w-fit"
                    >
                      Request a Quote <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </Reveal>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* AI PROCESS SCHEMA */}
      <section className="py-14 lg:py-16 bg-white">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
          <Reveal variant="up" className="max-w-2xl mb-12">
            <p className="eyebrow-teal">AI Process</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-[#071D49] leading-[1.15]">
              How we keep AI multilingual content accurate.
            </h2>
            <p className="mt-3 text-[14.5px] text-[#5B6472] leading-relaxed">
              A repeatable validation pipeline trusted by AI product teams shipping content in 85+ languages.
            </p>
          </Reveal>
          <ProcessSchema steps={AI_PROCESS} accent />
        </div>
      </section>
    </div>
  );
}
